import { Component } from 'react';
import cx from 'classnames';
import { getDescriptiveDateFromEpoch, getPossessiveForm } from '../../GlobalFunctions';
import styles from '../../SCSS/Header.module.scss';
import bellIcon from '../../images/pkp-bell.svg';
import { useHistory } from 'react-router-dom';
import { MessageType } from '@peekapak/types/DataProtocolTypes';
import { setMessageMark } from '../context/contextFunctions';

type Props = {
  toggle?: boolean;
  messages: MessageType[];
  messageMark: number;
  history: History;
};

export class NotificationBell extends Component<Props> {
  constructor(props: Props) {
    super(props);
    this.messageListLimit = 5;

    const now = new Date();
    this.withinXDaysIsRecent = 7;
    this.fromXDaysAgo = new Date(now.getFullYear(), now.getMonth(), now.getDate() - this.withinXDaysIsRecent);

    this.state = {
      toggle: Boolean(this.props.toggle),
    };

    this.toggleMenu = this.toggleMenu.bind(this);
    this.showAllNotifications = this.showAllNotifications.bind(this);
    this.handleClickNotificationMessage = this.handleClickNotificationMessage.bind(this);
  }

  toggleMenu(state) {
    const toggle = state !== undefined ? state : !this.state.toggle;
    if (toggle && !this.state.toggle) {
      this.setState({ toggle, markAtOpen: this.props.messageMark });
      this.markAllAsRead();
    } else {
      this.setState({ toggle });
    }
  }

  markAllAsRead() {
    const newest = this.props.messages.reduce((a, b) => (b.date > a.date ? b : a), this.props.messages[0]);
    if (newest) this.markAsReadIfNew(newest);
  }

  // While open, keep highlighting what was unread when the dropdown opened.
  displayMark() {
    return this.state.toggle ? (this.state.markAtOpen ?? this.props.messageMark) : this.props.messageMark;
  }

  handleActionClick = (event: React.MouseEvent<HTMLAnchorElement>, actionHref: string, message: MessageType) => {
    event.preventDefault();
    event.stopPropagation();

    this.toggleMenu(false);
    this.markAsReadIfNew(message);

    try {
      const url = new URL(actionHref, window.location.origin);
      const path = url.pathname + url.search + url.hash;
      this.props.history.push(path);
    } catch (error) {
      console.error('Failed to parse action URL:', error);
      window.location.href = actionHref;
    }
  };

  getEventMessage(message, i, computedClass) {
    if (message.type === 'ConcerningEmotionsUrgent') {
      const action = message.actions?.[0];
      return (
        <div className={computedClass} key={i} onClick={() => this.handleClickNotificationMessage(message)}>
          <div className={styles.urgentNotification}>
            <span className={styles.urgentNotificationIcon}>&#x26A0;&#xFE0F;</span>
            <span className={styles.urgentNotificationText}>{message.content}</span>
          </div>
          {action && (
            <a
              href={action.href}
              onClick={(event) => this.handleActionClick(event, action.href, message)}
              className={styles.urgentNotificationLink}
            >
              {action.label} &rarr;
            </a>
          )}
        </div>
      );
    }

    const hasActions = Array.isArray(message.actions) && message.actions.length > 0;

    return (
      <div className={computedClass} key={i} onClick={() => this.handleClickNotificationMessage(message)}>
        {message.content ? (
          <span className={cx(styles.notification, styles.normalText)}>{message.content}&nbsp;</span>
        ) : (
          <>
            <span className={cx(styles.notification, styles.author)}>
              {message.parentsName ? message.parentsName : `${getPossessiveForm(message.studentsName || '')} parent `}
              &nbsp;
            </span>
            <span className={cx(styles.notification, styles.verb)}>{message.verb}&nbsp;</span>
            <span className={cx(styles.notification, styles.noun)}>{message.noun}&nbsp;</span>
          </>
        )}
        <span className={cx(styles.notification, styles.normalText)}>
          {getDescriptiveDateFromEpoch(parseInt(message.date))}
        </span>
        {hasActions ? (
          <div style={{ marginTop: '8px', display: 'flex', gap: '8px' }}>
            {message.actions.map((action) => (
              <a
                key={action.id}
                href={action.href}
                onClick={(event) => this.handleActionClick(event, action.href, message)}
                style={{
                  display: 'inline-block',
                  padding: '4px 12px',
                  borderRadius: '4px',
                  background: action.id === 'accept' ? '#00afd8' : '#6b7280',
                  color: '#fff',
                  textDecoration: 'none',
                  fontSize: '1.2rem',
                  lineHeight: '1.6',
                  whiteSpace: 'nowrap',
                }}
              >
                {action.label}
              </a>
            ))}
          </div>
        ) : null}
      </div>
    );
  }

  isNewMessage(message) {
    return message.date > this.props.messageMark;
  }

  markAsReadIfNew(message: MessageType) {
    if (this.isNewMessage(message)) {
      setMessageMark(message.date).catch((error) => console.error('Failed to mark notification as read:', error));
    }
  }

  handleClickNotificationMessage(message: MessageType) {
    this.toggleMenu(false);
    this.markAsReadIfNew(message);
    this.props.history.push('/notifications');
  }

  generateMessageList() {
    const rows = [];
    const messageArray = this.props.messages;
    const messagesToShow = messageArray.filter(
      (message) => message.date > this.fromXDaysAgo || message.date > this.displayMark(),
    );

    if (messagesToShow.length) {
      for (let i = 0; i < this.messageListLimit && i < messagesToShow.length; i++) {
        const message = messagesToShow[i];
        const computedClass = cx(
          styles.tooltipBox,
          styles.notification,
          { [styles.noBorder]: i === 0 },
          { [styles.newMessage]: message.date > this.displayMark() },
        );
        rows.push(this.getEventMessage(message, i, computedClass));
      }
    } else {
      rows.push(
        // TODO: had no-border class here
        <div key='nomessages' className={cx(styles.tooltipBox, styles.notification)}>
          No messages from within the last {this.withinXDaysIsRecent} days
        </div>,
      );
    }

    if (messageArray.length > this.messageListLimit) {
      rows.push(
        <div
          className={cx(styles.tooltipBox, styles.notification, styles.moreMessagesPrompt)}
          key={'overflow'}
          onClick={this.showAllNotifications}
        >
          <div>
            {`+ ${messageArray.length - Math.min(this.messageListLimit, messagesToShow.length)} older messages`}
          </div>
        </div>,
      );
    }
    return rows;
  }

  showAllNotifications() {
    this.props.history.push('/notifications');
  }

  renderTooltip() {
    return <div className={cx(styles.headerTooltip, styles.notification)}>{this.generateMessageList()}</div>;
  }

  countNewMessages() {
    if (!this.props.messages) {
      return 0;
    }

    const messagesSinceMessageMark = this.props.messages.filter((message) => message.date > this.props.messageMark);
    return messagesSinceMessageMark.length;
  }

  render() {
    if (!this.props.messages) {
      return null;
    }

    if (this.countNewMessages() === 0 && !this.state.toggle) {
      return null;
    }

    return (
      <div
        className={cx(styles.headerButton, styles.HeaderNotification)}
        onClick={() => this.toggleMenu()}
        onMouseLeave={() => this.toggleMenu(false)}
      >
        <div className={styles.tooltipWrapper}>
          <div className={styles.badgeWrapper}>
            <img
              className={styles.pkpIcon}
              aria-hidden='true'
              alt='bell'
              src={bellIcon}
              style={{ width: '22px', height: '24px' }}
            />
            <span className={styles.badge} data-count={this.countNewMessages()}>
              {this.countNewMessages()}
            </span>
          </div>
          {this.state.toggle ? this.renderTooltip() : null}
        </div>
      </div>
    );
  }
}

function ClassFunctionWrapper<T>(props: T): JSX.Element {
  const history = useHistory();
  return <NotificationBell {...props} history={history} />;
}
export default ClassFunctionWrapper;
